import { pickStandingActionId, buildSmallSpriteUrl } from '../../../js/utils/spriteFallbacks.js';
import { normalizeGender } from '../../../js/utils/genderUtils.js';

export const sessionImageCache = new Map();

const generateBaseSpriteUrl = (characterId, actionId = null, gender = null) => {
  const resolvedActionId = actionId !== null ? actionId : pickStandingActionId(gender);
  return buildSmallSpriteUrl(characterId, resolvedActionId, 0);
};

const preloadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error(`Failed to preload: ${url}`));
  img.loading = 'lazy';
  img.fetchPriority = 'low';
  img.src = url;
});

const batchPreloadImages = (urls, options = {}) => {
  const { concurrency = 4 } = options;
  if (!urls?.length) return Promise.resolve([]);

  const urlsToLoad = [...urls];

  const loadBatch = async () => {
    const url = urlsToLoad.shift();
    if (!url) return;

    try {
      await preloadImage(url);
    } catch (error) {
      console.warn(`Error preloading image: ${url}`, error);
    }

    return loadBatch();
  };

  const loaders = Array(Math.min(concurrency, urlsToLoad.length))
    .fill(null)
    .map(() => loadBatch());

  Promise.all(loaders).catch(error => console.error('Error in batch preloading:', error));
  return Promise.resolve([]);
};

export const getMainSpritesOnly = (characterData, actionId = null) => {
  if (!characterData?.length) return {};

  const characters = characterData.map(c => ({
    id: String(c.id).trim(),
    bt: c.bt !== undefined && c.bt !== null ? parseInt(c.bt) || 603 : 603,
    gender: c.gender !== undefined ? c.gender : null
  }));

  const urls = {};
  const urlsToPreload = [];

  characters.forEach(char => {
    const key = `${char.id}_${normalizeGender(char.gender)}_${actionId || 'random'}`;

    if (sessionImageCache.has(key)) {
      const cachedMain = sessionImageCache.get(key);
      const cachedAlt = sessionImageCache.get(`${key}_alt`);
      urls[char.id] = {
        main: cachedMain,
        alternate: cachedAlt || null
      };
      return;
    }

    const displayUrl = generateBaseSpriteUrl(char.id, actionId, char.gender);
    sessionImageCache.set(key, displayUrl);
    urls[char.id] = { main: displayUrl, alternate: null };
    urlsToPreload.push(displayUrl);
  });

  if (urlsToPreload.length > 0) batchPreloadImages(urlsToPreload);

  return urls;
};

export const clearSpritePreviewCache = () => {
  sessionImageCache.clear();
};
